import { TrendingUp } from "lucide-react";

export interface WeeklyProgressCardProps {
  /** Treinos concluídos na semana. */
  completed: number;
  /** Meta semanal (default: 5). */
  goal?: number;
  /** Dias da semana (segunda a domingo) — true se houve treino completo. */
  weekDots?: boolean[];
  /** Streak atual (dias consecutivos com treino). */
  streakDays?: number;
}

const DAY_LABELS = ["S", "T", "Q", "Q", "S", "S", "D"] as const;

// Geometria do ring SVG (raio escolhido pra caber em 96×96 com stroke 8).
const RING_SIZE = 96;
const RING_STROKE = 8;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRC = 2 * Math.PI * RING_RADIUS;

/**
 * Card de progresso semanal: ring chart (% da meta) + bolinhas dos dias da
 * semana + streak. Portado de `frontend/components/portal/WeeklyProgressCard.tsx`
 * (Next), trocando o PieChart do recharts por um ring SVG puro (mais leve) e
 * usando as classes do tema novo.
 */
export function WeeklyProgressCard({
  completed,
  goal = 5,
  weekDots,
  streakDays,
}: WeeklyProgressCardProps) {
  const safeGoal = Math.max(1, goal);
  const pct = Math.min(100, Math.round((completed / safeGoal) * 100));
  const dashOffset = RING_CIRC * (1 - pct / 100);

  const message =
    pct >= 100
      ? "Meta semanal batida — segue assim."
      : pct >= 60
        ? "Bom ritmo. Falta pouco pra meta."
        : pct > 0
          ? "Bora? Cada treino conta."
          : "Semana ainda começando.";

  return (
    <section
      aria-label="Sua evolução esta semana"
      className="relative overflow-hidden rounded-3xl border border-border-subtle bg-gradient-to-br from-surface via-surface to-elevated p-5 shadow-lg"
    >
      {/* Glow accent */}
      <div
        aria-hidden
        className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-accent opacity-15 blur-3xl"
      />

      <div className="relative flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-medium uppercase tracking-wider text-faint">
            Sua semana
          </p>
          <h2 className="mt-0.5 font-display text-lg text-ink">
            {completed} de {safeGoal} treinos
          </h2>
          <p className="mt-1 text-xs text-muted">{message}</p>

          {/* Streak */}
          {streakDays != null && streakDays > 0 ? (
            <div className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-accent-soft/40 bg-accent/15 px-2.5 py-1 text-[11px] font-medium text-accent-soft">
              <TrendingUp size={11} aria-hidden />
              {streakDays} {streakDays === 1 ? "dia" : "dias"} de sequência
            </div>
          ) : null}
        </div>

        {/* Ring chart (SVG puro) */}
        <div className="relative h-24 w-24 shrink-0">
          <svg
            viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
            className="h-full w-full -rotate-90"
            aria-hidden
          >
            <circle
              cx={RING_SIZE / 2}
              cy={RING_SIZE / 2}
              r={RING_RADIUS}
              fill="none"
              strokeWidth={RING_STROKE}
              className="stroke-accent/15"
            />
            <circle
              cx={RING_SIZE / 2}
              cy={RING_SIZE / 2}
              r={RING_RADIUS}
              fill="none"
              strokeWidth={RING_STROKE}
              strokeLinecap="round"
              strokeDasharray={RING_CIRC}
              strokeDashoffset={dashOffset}
              className="stroke-accent-soft transition-[stroke-dashoffset] duration-700 ease-out"
            />
          </svg>
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <span className="font-display text-xl text-ink">{pct}%</span>
          </div>
        </div>
      </div>

      {/* Dots por dia da semana */}
      {weekDots && weekDots.length > 0 ? (
        <div
          className="relative mt-4 flex items-center justify-between"
          role="group"
          aria-label="Dias da semana"
        >
          {DAY_LABELS.map((label, i) => {
            const filled = weekDots[i] === true;
            return (
              <div key={`${label}-${i}`} className="flex flex-col items-center gap-1">
                <span
                  aria-hidden
                  className={
                    filled
                      ? "h-2.5 w-2.5 rounded-full bg-accent-soft shadow-sm"
                      : "h-2.5 w-2.5 rounded-full bg-border-strong"
                  }
                />
                <span
                  className={
                    filled
                      ? "text-[10px] font-medium text-accent-soft"
                      : "text-[10px] font-medium text-faint"
                  }
                >
                  {label}
                </span>
              </div>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}
